import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Loader2, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { SupplyAgentFamilyOverviewPanel } from '@/modules/supply/components/SupplyAgentFamilyOverviewPanel';
import { SupplyAgentSavedRulesList } from '@/modules/supply/components/SupplyAgentSavedRulesList';
import { SupplyAgentCompletionStrategyPicker } from '@/modules/supply/components/SupplyAgentCompletionStrategyPicker';
import { SupplyAgentReductionStrategyPicker } from '@/modules/supply/components/SupplyAgentReductionStrategyPicker';
import { formatAppliedRulesShort } from '@/modules/supply/components/supplyAgentRuleUtils';
import type { useSupplyAgentPersistedRules } from '@/modules/supply/hooks/useSupplyAgentPersistedRules';
import { isSessionEphemeralRuleEnabled } from '@/modules/supply/components/supplyAgentSessionLabels';

const CHANGES_PAGE_SIZE = 10;

interface SupplyAgentSimulatePanelProps {
  supplierId: number | null;
  warehouseId: number;
  supplierName?: string | null;
  persistedRules: ReturnType<typeof useSupplyAgentPersistedRules>;
  /** Sin marco externo (para embeber en el drawer del agente). */
  embedded?: boolean;
  /**
   * Solo listado/configuración de reglas (módulo Compras → Reglas).
   * Oculta impacto de pedido, overview de familia y líneas modificadas.
   */
  configOnly?: boolean;
  onSelectSupplier?: (supplierId: number, supplierName?: string) => void;
}

function formatQty(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(0);
}

export function SupplyAgentSimulatePanel({
  supplierId,
  warehouseId,
  supplierName,
  persistedRules,
  embedded = false,
  configOnly = false,
  onSelectSupplier,
}: SupplyAgentSimulatePanelProps) {
  const { t, i18n } = useTranslation();
  const { format: formatMoney, symbol: currencySymbol } = useCurrency();
  const warehouseMode = supplierId == null;
  const [showChanges, setShowChanges] = useState(false);
  const [changesPage, setChangesPage] = useState(0);

  const {
    rules: savedRules,
    loadingRules,
    simulating,
    deletingRuleId,
    togglingRuleId,
    sessionEnabledIds,
    sessionEphemeralRules,
    error,
    simulation: result,
    warehouseSimulation,
    deleteRule,
    purgeRule,
    purgeInactiveRules,
    setSessionRuleEnabled,
    setPersistedRuleActive,
    completionStrategy,
    setCompletionStrategy,
    ruleCompletionStrategies,
    setRuleCompletionStrategy,
    hasCompletionRule,
    reductionStrategy,
    setReductionStrategy,
    ruleReductionStrategies,
    setRuleReductionStrategy,
    hasReductionRule,
  } = persistedRules;

  useEffect(() => {
    if (result) {
      setShowChanges(result.changes.length > 0);
      setChangesPage(0);
    }
  }, [result]);

  const changesTotal = result?.changes.length ?? 0;
  const changesPageCount = Math.max(1, Math.ceil(changesTotal / CHANGES_PAGE_SIZE));
  const safeChangesPage = Math.min(changesPage, changesPageCount - 1);
  const pagedChanges = useMemo(() => {
    if (!result?.changes.length) return [];
    const start = safeChangesPage * CHANGES_PAGE_SIZE;
    return result.changes.slice(start, start + CHANGES_PAGE_SIZE);
  }, [result, safeChangesPage]);

  const fmt = (value: number) => formatMoney(value);
  const diffAmount =
    result != null ? result.after.total_amount_euros - result.before.total_amount_euros : 0;
  const diffItems =
    result != null ? result.after.total_items - result.before.total_items : 0;
  const busy = loadingRules || simulating;

  const warehouseSupplierRows = useMemo(() => {
    const rows = (warehouseSimulation?.by_supplier || []).filter(
      (row) => Number(row.before_value_euros) > 0,
    );
    return rows;
  }, [warehouseSimulation]);

  const warehouseSupplierRowsVisible = warehouseSupplierRows.slice(0, 15);

  const familyEphemeralRules = useMemo(
    () =>
      sessionEphemeralRules.filter((rule) => {
        if (!isSessionEphemeralRuleEnabled(rule)) return false;
        const rt = String(rule.rule_type || '').toLowerCase();
        return (
          rt === 'family_minimum_quantity' ||
          rt === 'family_maximum_quantity' ||
          rt === 'family_minimum_order_value'
        );
      }),
    [sessionEphemeralRules],
  );

  const hasActiveFamilyWarehouseRule = useMemo(
    () =>
      savedRules.some((rule) => {
        if (rule.is_active === false) return false;
        const rt = String(rule.rule_type || '').toLowerCase();
        return rt === 'family_minimum_quantity' || rt === 'family_maximum_quantity' || rt === 'family_minimum_order_value';
      }) || familyEphemeralRules.length > 0,
    [savedRules, familyEphemeralRules.length],
  );
  const hasWarehouseBudgetSim =
    warehouseSimulation != null &&
    warehouseSimulation.status === 'ok' &&
    (warehouseSimulation.budget_limit_euros != null ||
      warehouseSimulation.units_cap != null ||
      warehouseSimulation.minimum_order_euros != null);

  const hasWarehouseMinSim =
    warehouseSimulation != null &&
    warehouseSimulation.status === 'ok' &&
    warehouseSimulation.minimum_order_euros != null;

  const activeCompletionStrategy =
    completionStrategy ||
    result?.simulation_summary?.completion_strategy ||
    'coverage_layers';
  const activeReductionStrategy = reductionStrategy || 'slow_movers';
  const completionHintKey =
    activeCompletionStrategy === 'top_sales'
      ? 'supply.supplyAgent.completionCriterionHintTopSales'
      : activeCompletionStrategy === 'proportional'
        ? 'supply.supplyAgent.completionCriterionHintProportional'
        : activeCompletionStrategy === 'high_margin'
          ? 'supply.supplyAgent.completionCriterionHintHighMargin'
          : 'supply.supplyAgent.completionCriterionHintCoverage';
  const reductionHintKey =
    activeReductionStrategy === 'proportional'
      ? 'supply.supplyAgent.reductionCriterionHintProportional'
      : 'supply.supplyAgent.reductionCriterionHintSlowMovers';

  const warehouseHintKey = hasActiveFamilyWarehouseRule
    ? 'supply.supplyAgent.warehouseModeSimHintFamily'
    : hasWarehouseMinSim
      ? 'supply.supplyAgent.warehouseModeSimHintMin'
      : hasWarehouseBudgetSim
        ? 'supply.supplyAgent.warehouseModeSimHintBudget'
        : 'supply.supplyAgent.warehouseModeSimHint';

  // No acoplar a `simulating`: eso re-fetchaba el overview en cada simulación
  // (activar/desactivar/aplicar regla) y sentía el panel más lento.
  const familyOverviewRefreshToken = useMemo(
    () =>
      savedRules.length +
      familyEphemeralRules.length +
      savedRules.reduce((acc, r) => acc + (r.is_active === false ? 0 : 1), 0),
    [savedRules, familyEphemeralRules.length],
  );

  /** Solo filas que aportan algo que no diga ya el listado + tabla de líneas. */
  const insightfulContributions = useMemo(() => {
    const rows = result?.simulation_summary?.rule_contributions || [];
    return rows.filter(
      (row) =>
        row.contribution === 'order_satisfied' ||
        row.contribution === 'order_unmet' ||
        row.contribution === 'active',
    );
  }, [result]);

  return (
    <div
      className={
        embedded
          ? 'rounded-lg border border-slate-200 bg-slate-50/80 p-2.5'
          : 'mb-3 p-3 bg-gradient-to-r from-sky-50 to-white border border-sky-200 rounded-lg shadow-sm'
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {!embedded && (
            <h3 className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-sky-600" />
              {t('supply.supplyAgent.title')}
            </h3>
          )}
          {supplierName && (
            <p className={`text-xs text-gray-500 ${embedded ? '' : 'mt-0.5'}`}>
              {t('supply.supplyAgent.supplierContext', { name: supplierName })}
            </p>
          )}
          {embedded && !configOnly && busy && (
            <span className="inline-flex items-center gap-1.5 text-xs text-sky-700">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t('supply.supplyAgent.simulating')}
            </span>
          )}
        </div>
        {!embedded && !configOnly && busy && (
          <span className="inline-flex items-center gap-1.5 text-xs text-sky-700">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {t('supply.supplyAgent.simulating')}
          </span>
        )}
      </div>

      <div className="mt-2">
        <SupplyAgentSavedRulesList
          rules={savedRules}
          loading={loadingRules && savedRules.length === 0}
          allowDelete
          allowSessionToggle={!warehouseMode}
          allowPersistToggle
          sessionEnabledIds={sessionEnabledIds}
          deletingRuleId={deletingRuleId}
          togglingRuleId={togglingRuleId}
          onDeleteRule={deleteRule}
          onPurgeRule={purgeRule}
          onPurgeInactive={warehouseMode ? undefined : purgeInactiveRules}
          onSessionToggle={setSessionRuleEnabled}
          onPersistToggle={setPersistedRuleActive}
          orderCompletionStrategy={completionStrategy}
          ruleCompletionStrategies={ruleCompletionStrategies}
          onRuleCompletionStrategy={setRuleCompletionStrategy}
          completionPickerDisabled={simulating}
          orderReductionStrategy={reductionStrategy}
          ruleReductionStrategies={ruleReductionStrategies}
          onRuleReductionStrategy={setRuleReductionStrategy}
          reductionPickerDisabled={simulating}
        />
      </div>

      {hasCompletionRule && (
        <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/60 px-2.5 py-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-indigo-800">
            {t('supply.supplyAgent.completionCriterionTitle')}
          </p>
          <div className="mt-1.5">
            <SupplyAgentCompletionStrategyPicker
              value={activeCompletionStrategy}
              disabled={simulating}
              onChange={setCompletionStrategy}
            />
          </div>
          <p className="mt-1.5 text-xs text-indigo-950 leading-snug">{t(completionHintKey)}</p>
          <p className="mt-1 text-[11px] text-indigo-800/80 leading-snug">
            {t('supply.supplyAgent.completionCriterionPerRuleHint')}
          </p>
        </div>
      )}

      {hasReductionRule && (
        <div className="mt-2 rounded-md border border-amber-100 bg-amber-50/60 px-2.5 py-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-amber-900">
            {t('supply.supplyAgent.reductionCriterionTitle')}
          </p>
          <div className="mt-1.5">
            <SupplyAgentReductionStrategyPicker
              value={activeReductionStrategy}
              disabled={simulating}
              onChange={setReductionStrategy}
            />
          </div>
          <p className="mt-1.5 text-xs text-amber-950 leading-snug">{t(reductionHintKey)}</p>
          <p className="mt-1 text-[11px] text-amber-900/80 leading-snug">
            {t('supply.supplyAgent.reductionCriterionPerRuleHint')}
          </p>
        </div>
      )}

      {!configOnly && warehouseMode && (
        <p className="mt-2 rounded-md border border-sky-100 bg-sky-50/80 px-2.5 py-2 text-xs text-sky-900">
          {t(warehouseHintKey)}
        </p>
      )}

      {!configOnly && warehouseMode && (
        <div className="mt-2">
          <SupplyAgentFamilyOverviewPanel
            warehouseId={warehouseId}
            ephemeralRules={familyEphemeralRules}
            refreshToken={familyOverviewRefreshToken}
            embedded
            onSelectSupplier={onSelectSupplier}
          />
        </div>
      )}

      {!configOnly && warehouseMode && warehouseSimulation && warehouseSimulation.status === 'ok' && (
        <div className="mt-3 space-y-2 rounded-md border border-emerald-100 bg-white px-2.5 py-2">
          <p className="text-xs font-semibold text-slate-800">
            {t('supply.supplyAgent.warehouseImpactTitle')}
          </p>
          {warehouseSimulation.budget_limit_euros != null && (
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.before')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.before_value_euros)}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.after')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.after_value_euros)}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.delta')}</p>
                <p
                  className={`font-medium ${
                    warehouseSimulation.delta_value_euros < 0
                      ? 'text-emerald-700'
                      : warehouseSimulation.delta_value_euros > 0
                        ? 'text-amber-800'
                        : 'text-slate-900'
                  }`}
                >
                  {`${warehouseSimulation.delta_value_euros >= 0 ? '+' : ''}${fmt(
                    warehouseSimulation.delta_value_euros,
                  )}`}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.warehouseBudgetCap')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.budget_limit_euros)}
                </p>
              </div>
            </div>
          )}
          {warehouseSimulation.minimum_order_euros != null && (
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.before')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.before_value_euros)}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.after')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.after_value_euros)}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.delta')}</p>
                <p
                  className={`font-medium ${
                    warehouseSimulation.delta_value_euros > 0
                      ? 'text-amber-800'
                      : 'text-slate-900'
                  }`}
                >
                  {`${warehouseSimulation.delta_value_euros >= 0 ? '+' : ''}${fmt(
                    warehouseSimulation.delta_value_euros,
                  )}`}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.warehouseMinFloor')}</p>
                <p className="font-medium text-slate-900">
                  {fmt(warehouseSimulation.minimum_order_euros)}
                </p>
              </div>
            </div>
          )}
          {Number(warehouseSimulation.gap_to_minimum_euros || 0) > 0 && (
            <p className="text-xs text-amber-800">
              {t('supply.supplyAgent.warehouseMinGap', {
                gap: fmt(Number(warehouseSimulation.gap_to_minimum_euros || 0)),
              })}
            </p>
          )}
          {warehouseSimulation.units_cap != null && (
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.before')}</p>
                <p className="font-medium text-slate-900">
                  {Number(warehouseSimulation.before_units || 0).toLocaleString(i18n.language)}{' '}
                  {t('supply.supplyAgent.units')}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.after')}</p>
                <p className="font-medium text-slate-900">
                  {Number(warehouseSimulation.after_units || 0).toLocaleString(i18n.language)}{' '}
                  {t('supply.supplyAgent.units')}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.delta')}</p>
                <p
                  className={`font-medium ${
                    Number(warehouseSimulation.delta_units || 0) < 0
                      ? 'text-emerald-700'
                      : Number(warehouseSimulation.delta_units || 0) > 0
                        ? 'text-amber-800'
                        : 'text-slate-900'
                  }`}
                >
                  {`${Number(warehouseSimulation.delta_units || 0) >= 0 ? '+' : ''}${Number(
                    warehouseSimulation.delta_units || 0,
                  ).toLocaleString(i18n.language)} ${t('supply.supplyAgent.units')}`}
                </p>
              </div>
              <div>
                <p className="text-slate-500">{t('supply.supplyAgent.warehouseUnitsCap')}</p>
                <p className="font-medium text-slate-900">
                  {Number(warehouseSimulation.units_cap).toLocaleString(i18n.language)}{' '}
                  {t('supply.supplyAgent.units')}
                </p>
              </div>
            </div>
          )}
          {warehouseSimulation.excess_before_euros > 0 && (
            <p className="text-xs text-amber-800">
              {t('supply.supplyAgent.warehouseExcess', {
                excess: fmt(warehouseSimulation.excess_before_euros),
              })}
            </p>
          )}
          {Number(warehouseSimulation.excess_before_units || 0) > 0 && (
            <p className="text-xs text-amber-800">
              {t('supply.supplyAgent.warehouseUnitsExcess', {
                excess: Number(warehouseSimulation.excess_before_units || 0).toLocaleString(
                  i18n.language,
                ),
              })}
            </p>
          )}
          <p className="text-xs text-slate-500">
            {t(
              hasWarehouseMinSim && warehouseSimulation.budget_limit_euros == null
                ? 'supply.supplyAgent.warehouseAfterNoteMin'
                : 'supply.supplyAgent.warehouseAfterNote',
            )}
          </p>
          {warehouseSupplierRowsVisible.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs text-slate-700">
                <thead className="border-b border-slate-100 text-slate-500">
                  <tr>
                    <th className="py-1 pr-2 font-medium">
                      {t('supply.supplyAgent.warehouseSupplierCol')}
                    </th>
                    <th className="py-1 pr-2 font-medium">{t('supply.supplyAgent.before')}</th>
                    {hasWarehouseMinSim && (
                      <th className="py-1 pr-2 font-medium">
                        {t('supply.supplyAgent.warehouseSupplierShareMin')}
                      </th>
                    )}
                    <th className="py-1 pr-2 font-medium">{t('supply.supplyAgent.after')}</th>
                    <th className="py-1 font-medium">{t('supply.supplyAgent.delta')}</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouseSupplierRowsVisible.map((row) => (
                    <tr key={row.supplier_id} className="border-b border-slate-50">
                      <td className="py-1 pr-2 max-w-[10rem] truncate">
                        {onSelectSupplier ? (
                          <button
                            type="button"
                            onClick={() => onSelectSupplier(row.supplier_id, row.supplier_name)}
                            className="truncate text-left text-sky-800 hover:underline"
                            title={t('supply.supplyAgent.familySelectSupplier')}
                          >
                            {row.supplier_name}
                          </button>
                        ) : (
                          row.supplier_name
                        )}
                      </td>
                      <td className="py-1 pr-2">{fmt(row.before_value_euros)}</td>
                      {hasWarehouseMinSim && (
                        <td className="py-1 pr-2">
                          {row.share_minimum_euros != null
                            ? fmt(row.share_minimum_euros)
                            : '—'}
                        </td>
                      )}
                      <td className="py-1 pr-2">{fmt(row.after_value_euros)}</td>
                      <td
                        className={`py-1 ${
                          row.delta_value_euros < 0
                            ? 'text-emerald-700'
                            : row.delta_value_euros > 0
                              ? 'text-amber-800'
                              : ''
                        }`}
                      >
                        {`${row.delta_value_euros >= 0 ? '+' : ''}${fmt(row.delta_value_euros)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {warehouseSupplierRows.length > warehouseSupplierRowsVisible.length && (
                <p className="mt-1 text-xs text-slate-500">
                  {t('supply.supplyAgent.warehouseSupplierMore', {
                    shown: warehouseSupplierRowsVisible.length,
                    total: warehouseSupplierRows.length,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {!configOnly && result && (
        <div className="mt-3 pt-3 border-t border-sky-100">
          <p className="text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-md px-2 py-1.5">
            {t('supply.supplyAgent.tableWorkflowHint')}
          </p>

          {(result.family_global_summaries || []).map((summary, idx) => {
            const names = (summary.family_names || []).join(', ') || '—';
            const globalUnits = Number(summary.global_units || 0);
            const cap = summary.cap != null ? Number(summary.cap) : null;
            const cutHere = Number(summary.cut_here || 0);
            const remaining = Number(summary.remaining_elsewhere || 0);
            const bySupplier = summary.by_supplier || [];
            return (
              <div
                key={`family-summary-${idx}`}
                className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5"
              >
                <p>
                  {t('supply.supplyAgent.familyGlobalSummary', {
                    family: names,
                    global: globalUnits.toLocaleString(i18n.language),
                    cap: cap != null ? cap.toLocaleString(i18n.language) : '—',
                    cut: cutHere.toLocaleString(i18n.language),
                    remaining: remaining.toLocaleString(i18n.language),
                  })}
                </p>
                {summary.scope === 'supplier' ? (
                  <p className="mt-0.5 text-[11px] text-amber-800/80">
                    {t('supply.supplyAgent.familyScopeSupplierOnly')}
                  </p>
                ) : null}
                {bySupplier.length > 0 && (
                  <p className="mt-0.5 text-[11px] text-amber-800/80">
                    {t('supply.supplyAgent.familyBySupplier', {
                      detail: bySupplier
                        .map(
                          (row) =>
                            `${row.supplier_name}: ${Number(row.units || 0).toLocaleString(i18n.language)}`,
                        )
                        .join(' · '),
                    })}
                  </p>
                )}
              </div>
            );
          })}

          {result.commitment_summary && (() => {
            const cs = result.commitment_summary;
            const isMoney = cs.unit !== 'units';
            const unitLabel = isMoney ? currencySymbol : t('supply.supplyAgent.units');
            const fmtAmt = (n: number) =>
              isMoney
                ? formatMoney(n)
                : Math.round(n).toLocaleString(i18n.language);
            const monthsLeft = Number(cs.months_left || 0);
            const monthsAfter = Math.max(0, monthsLeft - 1);
            const status = cs.status || 'ok';
            const statusClass =
              status === 'needs_top_up'
                ? 'border-amber-200 bg-amber-50/80 text-amber-950'
                : status === 'fulfilled'
                  ? 'border-emerald-200 bg-emerald-50/70 text-emerald-950'
                  : 'border-emerald-100 bg-emerald-50/70 text-emerald-950';
            return (
              <div className={`mt-2 rounded-md border px-2.5 py-2 text-xs ${statusClass}`}>
                <p className="text-[11px] font-medium uppercase tracking-wide opacity-80">
                  {t('supply.supplyAgent.commitmentTitle', { year: cs.year })}
                </p>

                <div className="mt-1.5 space-y-1.5">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                      {t('supply.supplyAgent.commitmentYearHeading')}
                    </p>
                    <p className="mt-0.5 leading-snug">
                      {t('supply.supplyAgent.commitmentProgress', {
                        ytd: fmtAmt(Number(cs.ytd || 0)),
                        target: fmtAmt(Number(cs.period_target || 0)),
                        remaining: fmtAmt(Number(cs.remaining || 0)),
                        unit: unitLabel,
                      })}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                      {t('supply.supplyAgent.commitmentOrderHeading')}
                    </p>
                    <p className="mt-0.5 leading-snug">
                      {t('supply.supplyAgent.commitmentOrderQuota', {
                        quota: fmtAmt(Number(cs.order_target || 0)),
                        current: fmtAmt(Number(cs.current_order || 0)),
                        unit: unitLabel,
                      })}
                    </p>
                    {status !== 'fulfilled' && monthsLeft > 0 && (
                      <p className="mt-0.5 text-[11px] opacity-80 leading-snug">
                        {t('supply.supplyAgent.commitmentFutureNote', {
                          months: monthsLeft,
                          remaining: fmtAmt(Number(cs.remaining || 0)),
                          monthsAfter,
                          unit: unitLabel,
                        })}
                      </p>
                    )}
                  </div>
                </div>

                <p className="mt-1.5 text-[11px] font-semibold leading-snug">
                  {status === 'fulfilled'
                    ? t('supply.supplyAgent.commitmentStatusFulfilled')
                    : status === 'on_track'
                      ? t('supply.supplyAgent.commitmentStatusOnTrack', {
                          current: fmtAmt(Number(cs.current_order || 0)),
                          quota: fmtAmt(Number(cs.order_target || 0)),
                          unit: unitLabel,
                        })
                      : t('supply.supplyAgent.commitmentStatusNeedsTopUp', {
                          gap: fmtAmt(Number(cs.gap_to_order_target || 0)),
                          unit: unitLabel,
                        })}
                </p>
              </div>
            );
          })()}

          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div className="rounded-md bg-white border border-gray-200 p-2 min-w-0">
              <p className="text-[10px] uppercase text-gray-500 font-medium">
                {t('supply.supplyAgent.before')}
              </p>
              <p className="font-semibold text-gray-900 truncate">{fmt(result.before.total_amount_euros)}</p>
              <p className="text-[11px] text-gray-500 leading-snug">
                {t('supply.supplyAgent.linesSummary', {
                  lines: result.before.total_lines,
                  items: result.before.total_items,
                })}
              </p>
            </div>
            <div className="rounded-md bg-white border border-gray-200 p-2 min-w-0">
              <p className="text-[10px] uppercase text-gray-500 font-medium">
                {t('supply.supplyAgent.after')}
              </p>
              <p className="font-semibold text-gray-900 truncate">{fmt(result.after.total_amount_euros)}</p>
              <p className="text-[11px] text-gray-500 leading-snug">
                {t('supply.supplyAgent.linesSummary', {
                  lines: result.after.total_lines,
                  items: result.after.total_items,
                })}
              </p>
            </div>
            <div className="rounded-md bg-white border border-gray-200 p-2 min-w-0">
              <p className="text-[10px] uppercase text-gray-500 font-medium">
                {t('supply.supplyAgent.delta')}
              </p>
              <p
                className={`font-semibold truncate ${
                  diffAmount > 0 ? 'text-amber-700' : diffAmount < 0 ? 'text-green-700' : 'text-gray-900'
                }`}
              >
                {diffAmount >= 0 ? '+' : ''}
                {fmt(diffAmount)}
              </p>
              <p className="text-[11px] text-gray-500 leading-snug">
                {diffItems >= 0 ? '+' : ''}
                {diffItems} {t('supply.supplyAgent.units')}
              </p>
            </div>
          </div>

          {result.changes.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowChanges((prev) => !prev)}
                className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
              >
                {showChanges ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {t('supply.supplyAgent.toggleChanges', { count: result.changes.length })}
              </button>
              {showChanges && (
                <div
                  className={`mt-2 rounded-lg border border-gray-200 bg-white ${
                    embedded ? '' : 'overflow-x-auto'
                  }`}
                >
                  <table className="w-full table-fixed divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-[18%] px-2 py-1.5 text-left font-medium uppercase text-gray-500">
                          {t('supply.table.code')}
                        </th>
                        <th className="px-2 py-1.5 text-left font-medium uppercase text-gray-500">
                          {t('supply.table.product')}
                        </th>
                        {!embedded && (
                          <th className="px-2 py-1.5 text-left font-medium uppercase text-gray-500">
                            {t('supply.table.supplier')}
                          </th>
                        )}
                        <th className="w-[12%] px-2 py-1.5 text-right font-medium uppercase text-gray-500">
                          {t('supply.supplyAgent.qtyBefore')}
                        </th>
                        <th className="w-[12%] px-2 py-1.5 text-right font-medium uppercase text-gray-500">
                          {t('supply.supplyAgent.qtyAfter')}
                        </th>
                        <th className="w-[16%] px-2 py-1.5 text-right font-medium uppercase text-gray-500">
                          {t('supply.supplyAgent.valueDelta')}
                        </th>
                        <th className="px-2 py-1.5 text-left font-medium uppercase text-gray-500">
                          {t('supply.supplyAgent.changeReason')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagedChanges.map((change) => {
                        const shortRules = formatAppliedRulesShort(change.reasons);
                        const fullReasons =
                          (change.reasons || []).map((r) => r.label).filter(Boolean).join(' · ') ||
                          undefined;
                        return (
                        <tr key={change.product_id}>
                          <td className="break-all px-2 py-1 text-gray-800">{change.sku}</td>
                          <td className="px-2 py-1 text-gray-700" title={change.product_name || ''}>
                            {change.product_name || '—'}
                          </td>
                          {!embedded && (
                            <td
                              className="max-w-[120px] truncate px-2 py-1 text-gray-600"
                              title={change.supplier_name || supplierName || ''}
                            >
                              {change.supplier_name || supplierName || '—'}
                            </td>
                          )}
                          <td className="px-2 py-1 text-right text-gray-600">
                            {formatQty(change.old_quantity)}
                          </td>
                          <td className="px-2 py-1 text-right font-medium text-gray-900">
                            {formatQty(change.new_quantity)}
                          </td>
                          <td className="px-2 py-1 text-right text-gray-600">
                            {fmt(change.new_value_euros - change.old_value_euros)}
                          </td>
                          <td
                            className="px-2 py-1 text-gray-600"
                            title={fullReasons}
                          >
                            {shortRules || '—'}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {changesTotal > CHANGES_PAGE_SIZE && (
                    <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => setChangesPage((p) => Math.max(0, p - 1))}
                        disabled={safeChangesPage <= 0}
                        className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        {t('supply.supplyAgent.changesPrev')}
                      </button>
                      <span className="text-[11px] text-gray-500">
                        {t('supply.supplyAgent.changesPage', {
                          page: safeChangesPage + 1,
                          pages: changesPageCount,
                          from: safeChangesPage * CHANGES_PAGE_SIZE + 1,
                          to: Math.min((safeChangesPage + 1) * CHANGES_PAGE_SIZE, changesTotal),
                          total: changesTotal,
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setChangesPage((p) => Math.min(changesPageCount - 1, p + 1))
                        }
                        disabled={safeChangesPage >= changesPageCount - 1}
                        className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {t('supply.supplyAgent.changesNext')}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {result.changes.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">
              {result.commitment_summary?.status === 'on_track' ||
              result.commitment_summary?.status === 'fulfilled'
                ? t('supply.supplyAgent.noChangesCommitmentOk')
                : t('supply.supplyAgent.noChanges')}
            </p>
          )}

          {insightfulContributions.length > 0 && (
            <div className="mt-2 rounded-md border border-violet-100 bg-violet-50/60 px-2.5 py-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-violet-800">
                {t('supply.supplyAgent.ruleContributionsTitle')}
              </p>
              <ul className="mt-1 space-y-1">
                {insightfulContributions.map((row) => {
                  const key = `${row.rule_type || 'rule'}-${row.rule_id ?? row.label}`;
                  let suffix = t('supply.supplyAgent.ruleContributionActive');
                  if (row.contribution === 'order_satisfied') {
                    suffix = t('supply.supplyAgent.ruleContributionOrderSatisfied');
                  } else if (row.contribution === 'order_unmet') {
                    suffix = t('supply.supplyAgent.ruleContributionOrderUnmet');
                  }
                  return (
                    <li key={key} className="text-xs text-violet-950 leading-snug">
                      <span className="font-medium">{row.label}</span>
                      <span className="text-violet-800/90"> — {suffix}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!hasCompletionRule &&
            ((result.simulation_summary?.strategy_explanations || []).length > 0 ||
              result.simulation_summary?.completion_strategy) && (
              <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/60 px-2.5 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-indigo-800">
                  {t('supply.supplyAgent.completionCriterionTitle')}
                </p>
                <p className="mt-1 text-xs text-indigo-950 leading-snug">
                  {(result.simulation_summary?.strategy_explanations || [])[0] || t(completionHintKey)}
                </p>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
